import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  IconButton,
  Collapse,
  Paper,
  Divider
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  Home as HomeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useGraphQL } from '../../apollo/useGraphQL';
import { getFullPath } from '../../api/driveApi';

const PathDisplay = ({ selectedItem, showTrash = false, onNavigateToFolder, currentBreadcrumbs = [] }) => {
  const { executeQuery } = useGraphQL();
  const [fullPath, setFullPath] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedItem?.uuid) {
      loadFullPath();
    } else {
      setFullPath([]);
    }
  }, [selectedItem, showTrash]);

  const loadFullPath = async () => {
    if (!selectedItem?.uuid || showTrash) {
      setFullPath([]);
      return;
    }
    
    setLoading(true);
    try {
      const pathData = await getFullPath(
        executeQuery, 
        selectedItem.uuid, 
        selectedItem.extension
      );
      setFullPath(pathData || []);
    } catch (error) {
      console.error('Error loading full path:', error);
      setFullPath([]);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayName = (item) => {
    if (!item.extension) {
      return item.name;
    } else {
      return `${item.name}.${item.extension}`;
    }
  };

  const getPathBreadcrumbs = () => {
    const breadcrumbs = [];
    
    // 루트 추가
    if (showTrash) {
      breadcrumbs.push({
        name: '휴지통',
        uuid: null,
        isRoot: true
      });
    } else {
      breadcrumbs.push({
        name: '사내 드라이브',
        uuid: null,
        isRoot: true
      });
    }

    // 풀 경로 추가 (API에서 받은 순서 그대로 사용)
    if (fullPath.length > 0) {
      breadcrumbs.push(...fullPath);
    }


    return breadcrumbs;
  };

  const handlePathClick = (item) => {
    if (onNavigateToFolder && (!item.extension || item.isRoot)) {
      // 폴더이거나 루트인 경우에만 네비게이션 허용
      onNavigateToFolder(item.uuid, item.name);
    }
  };

  // 선택된 항목이 없으면 표시하지 않음
  // 또는 사내 드라이브 루트에 있고 휴지통이 아닐 때 표시하지 않음
  if (!selectedItem) {
    return null;
  }
  
  // 사내 드라이브 루트에서는 표시하지 않음 (currentBreadcrumbs 길이로 판단)
  if (!showTrash && currentBreadcrumbs.length <= 1) {
    return null;
  }

  const breadcrumbs = getPathBreadcrumbs();
  const shouldShowToggle = breadcrumbs.length > 3; // 3개 이상일 때 토글 표시

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: '#f8f9fa',
        borderTop: 'none',
        borderRadius: 0,
        minHeight: '48px',
        display: 'flex',
        alignItems: 'center',
        px: 3,
        py: 1,
        marginLeft: '224px', // 드로어 너비만큼 마진 적용
        marginRight: selectedItem ? '260px' : '0px', // 파일 상세 패널이 있을 때만 마진 적용 (거리 포함)
        transition: 'margin-right 0.3s ease'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
        {/* 선택된 항목 정보 */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, flexShrink: 0 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#5f6368',
              fontWeight: 500,
              mr: 1
            }}
          >
            {selectedItem.extension ? '파일' : '폴더'} 선택됨:
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#202124',
              fontWeight: 400,
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {getDisplayName(selectedItem)}
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

        {/* 경로 표시 영역 */}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          {loading ? (
            <Typography variant="body2" color="text.secondary">
              경로 로딩 중...
            </Typography>
          ) : (
            <>
              {/* 토글 버튼 (경로가 길 때만 표시) */}
              {shouldShowToggle && (
                <IconButton
                  size="small"
                  onClick={() => setExpanded(!expanded)}
                  sx={{ mr: 1, p: 0.5 }}
                >
                  {expanded ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
              )}

              {/* 경로 표시 */}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Collapse in={!shouldShowToggle || expanded} timeout="auto">
                  <Breadcrumbs 
                    separator={<ChevronRightIcon fontSize="small" sx={{ color: '#5f6368' }} />}
                    sx={{ 
                      '& .MuiBreadcrumbs-ol': {
                        flexWrap: expanded ? 'wrap' : 'nowrap',
                        overflow: expanded ? 'visible' : 'hidden'
                      }
                    }}
                  >
                    {breadcrumbs.map((item, index) => {
                      const isClickable = !item.extension || item.isRoot; // 폴더이거나 루트만 클릭 가능
                      const isLastItem = index === breadcrumbs.length - 1;
                      
                      return (
                        <Box
                          key={item.uuid || 'root'}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            minWidth: 0,
                            cursor: isClickable && !isLastItem ? 'pointer' : 'default'
                          }}
                          onClick={() => !isLastItem && handlePathClick(item)}
                        >
                          {item.isRoot && (
                            <HomeIcon 
                              sx={{ 
                                mr: 0.5, 
                                fontSize: 16, 
                                color: '#5f6368' 
                              }} 
                            />
                          )}
                          {!item.isRoot && !item.extension && (
                            <FolderIcon 
                              sx={{ 
                                mr: 0.5, 
                                fontSize: 16, 
                                color: '#5f6368' 
                              }} 
                            />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              color: isClickable && !isLastItem ? '#1a73e8' : '#5f6368',
                              fontSize: '13px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: expanded ? 'none' : '150px',
                              '&:hover': isClickable && !isLastItem ? {
                                textDecoration: 'underline'
                              } : {}
                            }}
                          >
                            {getDisplayName(item)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Breadcrumbs>
                </Collapse>

                {/* 축약된 경로 표시 (토글이 필요하고 접혀있을 때) */}
                {shouldShowToggle && !expanded && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {/* 첫 번째와 마지막 항목만 표시 */}
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                      onClick={() => handlePathClick(breadcrumbs[0])}
                    >
                      <HomeIcon sx={{ mr: 0.5, fontSize: 16, color: '#5f6368' }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#1a73e8', 
                          fontSize: '13px',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {breadcrumbs[0]?.name}
                      </Typography>
                    </Box>
                    <Typography sx={{ mx: 1, color: '#5f6368' }}>...</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {!breadcrumbs[breadcrumbs.length - 1]?.extension && (
                        <FolderIcon sx={{ mr: 0.5, fontSize: 16, color: '#5f6368' }} />
                      )}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#5f6368', 
                          fontSize: '13px',
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {getDisplayName(breadcrumbs[breadcrumbs.length - 1])}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default PathDisplay;